import { ReactNode, useRef } from "react"
import { Link, useForm } from "@inertiajs/react"
import Button from "@/components/Button"
import DefaultLayout from "@/layouts/DefaultLayout"
import Container from "@/components/Container"
import logoIcon from "@/assets/img/logo-icon.png"
import ErrorMessage from "@/components/ErrorMessage"
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"

const LoginPage = () => {
  const { data, setData, post, processing, errors } = useForm({
    email: "",
    turnstile_token: "",
  })

  const turnstileRef = useRef<TurnstileInstance>(null)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/login", {
      async: true,
      onError: () => turnstileRef.current?.reset(),
    })
  }

  return (
    <Container className="flex min-h-[calc(100vh-90px)] flex-1 justify-center pt-12 sm:pt-24">
      <div className="flex w-full max-w-sm flex-col items-center">
        <img
          src={logoIcon}
          alt=""
          width={56}
          height={56}
          className="size-12 rounded-xl object-cover sm:size-14"
        />
        <h1 className="mt-6 text-center text-2xl font-bold text-gray-900 sm:text-3xl">
          Log in to your account
        </h1>

        <form onSubmit={handleSubmit} className="mt-8 w-full">
          <div>
            <label
              htmlFor="email"
              className="block pb-1.5 text-sm font-medium text-gray-700"
            >
              Email
            </label>
            <div>
              <input
                name="email"
                type="email"
                id="email"
                autoComplete="email"
                inputMode="email"
                required
                className="focus:ring-chessdrop-600 block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
              />
            </div>
          </div>

          {turnstileSiteKey && (
            <Turnstile
              ref={turnstileRef}
              siteKey={turnstileSiteKey}
              options={{ action: "login", appearance: "interaction-only" }}
              onSuccess={(token) => setData("turnstile_token", token)}
              className="mt-4"
            />
          )}

          <Button
            loading={processing}
            size="lg"
            type="submit"
            className="mt-6"
            fullWidth
          >
            Login
          </Button>

          <ErrorMessage className="mt-5 text-center" message={errors.email} />
          {/* The server may return a `turnstile` error key outside the form fields */}
          <ErrorMessage
            className="mt-3 text-center"
            message={(errors as Record<string, string | undefined>).turnstile}
          />

          <p className="mt-8 text-center text-sm text-gray-600">
            Don’t have an account?{" "}
            <Link
              href="/signup"
              className="text-chessdrop-600 font-medium hover:underline"
            >
              Sign up
            </Link>
          </p>
        </form>
      </div>
    </Container>
  )
}

export default LoginPage

LoginPage.layout = (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>
